import { useEffect, useId, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}

export function Modal({ title, onClose, children, footer, width }: ModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={width ? ({ '--modal-w': `${width}px` } as React.CSSProperties) : undefined}
      >
        <header className="panel__header">
          <h2>{title}</h2>
          <button type="button" className="btn btn--ghost btn--icon" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>
        <div className="panel__body">{children}</div>
        {footer ? <footer className="panel__footer">{footer}</footer> : null}
      </div>
    </div>
  );
}

interface ConfirmProps {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  /** When set, the confirm button stays disabled until this exact text is typed. */
  confirmText?: string;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({ title, message, confirmLabel = 'Confirm', danger, busy, confirmText, onConfirm, onClose }: ConfirmProps) {
  const inputId = useId();
  const [typed, setTyped] = useState('');
  const ready = !busy && (confirmText === undefined || typed.trim() === confirmText);
  return (
    <Modal
      title={title}
      onClose={onClose}
      width={440}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={`btn ${danger ? 'btn--danger' : 'btn--primary'}`} onClick={onConfirm} disabled={!ready}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      <div>{message}</div>
      {confirmText !== undefined ? (
        <div className="confirm-text">
          <label htmlFor={inputId}>
            Type <code>{confirmText}</code> to confirm.
          </label>
          <input
            id={inputId}
            className="input mono"
            value={typed}
            autoFocus
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && ready) onConfirm();
            }}
          />
        </div>
      ) : null}
    </Modal>
  );
}
